import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Context Providers
import { AuthProvider, AuthContext } from './store/AuthContext';
import { ThemeProvider } from './store/ThemeContext';
import { DealDataProvider } from './store/DealDataContext';
import { CategoriesProvider } from './store/CategoriesContext';

// Services
import { analytics } from './services/googleAnalytics';
import { lcpPreloader } from './utils/lcpPreloader';
import { setupGlobalErrorHandling } from './utils/errorTracking';

// WebSocket
import { useWebSocket } from './hooks/useWebSocket';

// Feature Flags
import { featureFlags } from './utils/featureFlags';

// Development Tools
import { ServiceMonitor } from './components/debug/ServiceMonitor';
import { ErrorDashboard } from './components/debug/ErrorDashboard';
import { DevErrorMonitor } from './utils/devErrorMonitor';

// Error Tracking
import { initializeSentry, setUserContext } from './config/sentry';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { log } from './utils/logger';
import { shouldEnableDebugLogging, logEnvironmentConfig } from './utils/environmentConfig';

// Static Page Imports
import Home from './pages/Home';
import NotFound from './pages/NotFound'; // NotFound can often stay in main bundle or be lazy-loaded too

// Component Imports
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/routing/ProtectedRoute';
import AdminRoute from './components/routing/AdminRoute';
import OrganizationStructuredData from './components/common/OrganizationStructuredData';

// Import PageSkeleton for better loading experience
import PageSkeleton from './components/skeletons/PageSkeleton';

// Lazy-loaded Page Components
const AllDealsPage = React.lazy(() => import('./pages/AllDealsPage'));
const TrendingPage = React.lazy(() => import('./pages/TrendingPage'));
const SearchResultsPage = React.lazy(() => import('./pages/SearchResultsPage'));
const About = React.lazy(() => import('./pages/About'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const CommunityGuidelinesPage = React.lazy(() => import('./pages/CommunityGuidelinesPage'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const DealDetails = React.lazy(() => import('./pages/DealDetails'));
const Profile = React.lazy(() => import('./pages/Profile'));
const EditProfile = React.lazy(() => import('./pages/EditProfile'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
// Legacy CategoryPage removed - using SubcategoryPage for all category routes
const SubcategoryPage = React.lazy(() => import('./pages/SubcategoryPage'));
const SavedDealsPage = React.lazy(() => import('./pages/SavedDealsPage'));
const Badges = React.lazy(() => import('./pages/Badges'));
const Settings = React.lazy(() => import('./pages/Settings'));
const SubmitDeal = React.lazy(() => import('./pages/SubmitDeal'));

// Lazy-loaded Admin Page Components
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const DealApproval = React.lazy(() => import('./pages/admin/DealApproval'));
const DealLifecycleManagement = React.lazy(() => import('./pages/admin/DealLifecycleManagement'));
const AffiliateStats = React.lazy(() => import('./pages/admin/AffiliateStats'));
const AdvancedAffiliateStats = React.lazy(() => import('./pages/admin/AdvancedAffiliateStats'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const UserDetail = React.lazy(() => import('./pages/admin/UserDetail'));
const Analytics = React.lazy(() => import('./pages/admin/Analytics'));
const SEODashboard = React.lazy(() => import('./pages/admin/SEODashboard'));
const CategoryManagement = React.lazy(() => import('./pages/admin/CategoryManagement'));
const AffiliatePartners = React.lazy(() => import('./pages/admin/AffiliatePartners'));

// Lazy-loaded User Components
const UserAffiliateDashboard = React.lazy(() => import('./pages/UserAffiliateDashboard'));
const RecentlyViewedPage = React.lazy(() => import('./pages/RecentlyViewedPage'));

// Article Pages
const ArticlesListPage = React.lazy(() => import('./pages/ArticlesListPage'));
const ArticleDetailPage = React.lazy(() => import('./pages/ArticleDetailPage'));

// Brand and Store Pages
const BrandPage = React.lazy(() => import('./pages/BrandPage'));
const StorePage = React.lazy(() => import('./pages/StorePage'));
const BrandsDirectoryPage = React.lazy(() => import('./pages/BrandsDirectoryPage'));
const StoresDirectoryPage = React.lazy(() => import('./pages/StoresDirectoryPage'));
const CategoriesDirectoryPage = React.lazy(() => import('./pages/CategoriesDirectoryPage'));

// Contact Page
const ContactPage = React.lazy(() => import('./pages/ContactPage'));

// Development/Staging only routes
const ColorTest = React.lazy(() => import('./pages/ColorTest'));
const ComponentPreview = React.lazy(() => import('./pages/ComponentPreview'));

// Analytics tracking component
const AnalyticsTracker: React.FC = () => {
  const { user, isAuthenticated } = React.useContext(AuthContext);
  const location = useLocation();

  // Initialize WebSocket connection only if real-time features are enabled
  const { isConnected, authenticate, subscribeToDeals, subscribeToNotifications } = useWebSocket({
    autoConnect: featureFlags.realtimeEnabled, // Only connect if feature is enabled
    onConnect: () => {
      log.info('WebSocket connected successfully', { component: 'AnalyticsTracker' });
    },
    onDisconnect: (reason) => {
      log.warn('WebSocket disconnected', { component: 'AnalyticsTracker', reason });
    },
    onError: (error) => {
      log.error('WebSocket error', error, { component: 'AnalyticsTracker' });
    },
  });

  // Initialize Google Analytics once - NO MORE COMPLEX ANALYTICS
  React.useEffect(() => {
    analytics.init();

    // Set up global error handling
    setupGlobalErrorHandling();

    // Set user if authenticated
    if (isAuthenticated && user?.id) {
      analytics.setUser(user.id);
    }

    // Initial LCP preloading for home page
    if (location.pathname === '/') {
      lcpPreloader.preloadForRoute('/');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update user when authentication state changes
  React.useEffect(() => {
    // Update Sentry user context
    setUserContext({
      id: user?.id,
      email: user?.email,
      username: user?.username,
      isAuthenticated,
    });

    // Set GA user
    if (isAuthenticated && user?.id) {
      analytics.setUser(user.id);
    }

    // Authenticate WebSocket and subscribe to real-time updates (only if feature is enabled)
    if (
      featureFlags.realtimeEnabled &&
      isAuthenticated &&
      user &&
      user.id &&
      user.username &&
      isConnected
    ) {
      authenticate(user.id, user.username);
      subscribeToDeals();
      subscribeToNotifications(user.id);

      if (process.env.NODE_ENV === 'development') {
        log.debug('WebSocket authenticated and subscribed', {
          component: 'AnalyticsTracker',
          userId: user.id,
          username: user.username,
        });
      }
    }
  }, [
    isAuthenticated,
    user,
    isConnected,
    authenticate,
    subscribeToDeals,
    subscribeToNotifications,
  ]);

  // Track page views (simple Google Analytics)
  React.useEffect(() => {
    analytics.trackPageView(location.pathname);

    // Preload critical images for LCP optimization
    lcpPreloader.preloadForRoute(location.pathname);
  }, [location]);

  // Cleanup on page unload (no more analytics session management)
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      lcpPreloader.cleanup(); // Cleanup preloaded resources
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location]);

  return null;
};

// Scroll to top component
const ScrollToTop: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return null;
};

function App() {
  // Initialize error tracking and monitoring
  React.useEffect(() => {
    // Initialize Sentry error tracking
    initializeSentry();

    // Environment-aware configuration logging
    if (shouldEnableDebugLogging()) {
      logEnvironmentConfig();
      log.info('Application started', {
        component: 'App',
        environment: process.env.NODE_ENV,
        version: process.env.REACT_APP_VERSION || '1.0.0',
        realtimeEnabled: featureFlags.realtimeEnabled,
      });
    }

    // Log feature flag status
    log.info('Feature flags initialized', {
      component: 'App',
      realtimeEnabled: featureFlags.realtimeEnabled,
    });

    // Initialize development error monitoring
    if (process.env.NODE_ENV === 'development') {
      DevErrorMonitor.setupMonitoring();
      if (shouldEnableDebugLogging()) {
        log.debug('Development error monitoring enabled');
      }
    }
  }, []);

  return (
    <ErrorBoundary level="critical" context="Application Root">
      <ThemeProvider>
        <AuthProvider>
          <CategoriesProvider>
            <DealDataProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <AnalyticsTracker />
                <ScrollToTop />
                <OrganizationStructuredData />
                <ErrorBoundary level="page" context="Main Layout">
                  <div className="app-container">
                    <ErrorBoundary level="component" context="Navigation">
                      <Navbar />
                    </ErrorBoundary>
                    <main className="container">
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route
                          path="/deals"
                          element={
                            <React.Suspense fallback={<PageSkeleton />}>
                              <AllDealsPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/trending"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <TrendingPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/search"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <SearchResultsPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/about"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <About />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/faq"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <FAQPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/privacy"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <PrivacyPolicyPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/terms"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <TermsOfServicePage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/guidelines"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <CommunityGuidelinesPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/contact"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <ContactPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/articles"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <ArticlesListPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/articles/:slug"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <ArticleDetailPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/login"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <Login />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/register"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <Register />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/forgot-password"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <ForgotPassword />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/reset-password/:resettoken"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <ResetPassword />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/deals/:id/:slug?"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <DealDetails />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/categories"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <CategoriesDirectoryPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/categories/:slug"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <SubcategoryPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/categories/:slug1/:slug2"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <SubcategoryPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/categories/:slug1/:slug2/:slug3"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <SubcategoryPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/brands"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <BrandsDirectoryPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/brands/:slug"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <BrandPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/stores"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <StoresDirectoryPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/stores/:slug"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <StorePage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/submit"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading page...</div>}>
                                <SubmitDeal />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading...</div>}>
                                <Profile />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/user/:id"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <Profile />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/edit-profile"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading...</div>}>
                                <EditProfile />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notifications"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading...</div>}>
                                <Notifications />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/saved"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading...</div>}>
                                <SavedDealsPage />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/recently-viewed"
                          element={
                            <React.Suspense fallback={<div>Loading...</div>}>
                              <RecentlyViewedPage />
                            </React.Suspense>
                          }
                        />
                        <Route
                          path="/badges"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading...</div>}>
                                <Badges />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading...</div>}>
                                <Settings />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />

                        {/* Admin Routes */}
                        <Route
                          path="/admin/dashboard"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <Dashboard />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/deals/approval"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <DealApproval />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/deals/lifecycle"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <DealLifecycleManagement />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/affiliate-stats"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <AffiliateStats />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/users"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <UserManagement />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/users/:id"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <UserDetail />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/analytics"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <Analytics />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/seo"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading SEO Dashboard...</div>}>
                                <SEODashboard />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/categories"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <CategoryManagement />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="/admin/affiliate-partners"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <AffiliatePartners />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />

                        <Route
                          path="/admin/advanced-affiliate-stats"
                          element={
                            <AdminRoute>
                              <React.Suspense fallback={<div>Loading Admin...</div>}>
                                <AdvancedAffiliateStats />
                              </React.Suspense>
                            </AdminRoute>
                          }
                        />

                        <Route
                          path="/user-affiliate-dashboard"
                          element={
                            <ProtectedRoute>
                              <React.Suspense fallback={<div>Loading...</div>}>
                                <UserAffiliateDashboard />
                              </React.Suspense>
                            </ProtectedRoute>
                          }
                        />

                        {/* Development/Staging only routes */}
                        {process.env.NODE_ENV !== 'production' && (
                          <>
                            <Route
                              path="/color-test"
                              element={
                                <React.Suspense fallback={<div>Loading...</div>}>
                                  <ColorTest />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="/component-preview"
                              element={
                                <React.Suspense fallback={<div>Loading...</div>}>
                                  <ComponentPreview />
                                </React.Suspense>
                              }
                            />
                          </>
                        )}

                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </main>
                    <ErrorBoundary level="component" context="Footer">
                      <Footer />
                    </ErrorBoundary>
                  </div>
                  {process.env.NODE_ENV === 'development' && <ServiceMonitor />}
                  {process.env.NODE_ENV === 'development' && <ErrorDashboard />}
                </ErrorBoundary>
              </Router>
            </DealDataProvider>
          </CategoriesProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
